import { Award, ShieldCheck, Users, ArrowRight } from "lucide-react";
import marlonRibeiro from "@/assets/marlon-ribeiro.png";


export const Persona = () => {
  return (
    <section
      id="quem-vai-te-guiar"
      className="relative pb-16 md:pb-20 overflow-hidden"
    >
      {/* Glow de fundo suave */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-background to-background" />
        <div className="absolute -top-40 left-1/2 h-72 w-72 -translate-x-1/2 rounded-full bg-primary/25 blur-3xl opacity-70" />
      </div>

      <div className="container px-4">
        {/* Título / intro */}
        <div className="text-center mb-10 md:mb-14 animate-slide-up">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-primary mb-4">
            Sobre o especialista
          </div>
          <h2 className="text-3xl md:text-5xl font-bold mb-3">
            <span className="bg-gradient-accent bg-clip-text text-transparent drop-shadow-[0_0_18px_rgba(100,192,242,0.6)]">
              Quem vai te guiar?
            </span>
          </h2>
          <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
            Antes de confiar em um método, é importante saber quem está por
            trás dele. Conheça a história de quem dedicou a vida a tirar
            pessoas do vício em apostas.
          </p>
        </div>

        {/* Card principal */}
        <div className="mx-auto max-w-5xl animate-slide-up [animation-delay:120ms]">
          <div className="relative flex flex-col md:flex-row gap-8 md:gap-10 rounded-3xl border border-primary/25 bg-card/80 backdrop-blur supports-[backdrop-filter]:bg-card/60 px-6 py-8 md:px-10 md:py-10 shadow-[0_18px_60px_rgba(0,0,0,0.55)]">
            {/* Glow interno */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 opacity-80"
              style={{
                background:
                  "radial-gradient(900px 260px at 0% 0%, hsl(var(--primary)/0.18), transparent 65%)",
              }}
            />

            {/* Coluna esquerda: avatar + credenciais */}
            <div className="relative flex flex-col items-center md:items-start md:w-[36%] gap-4">
              {/* Avatar / foto (você pode trocar pelo <Image /> real depois) */}
              <div className="relative">
                <div className="h-32 w-32 md:h-36 md:w-36 rounded-full bg-gradient-to-tr from-primary via-primary/70 to-accent p-[3px] shadow-lg shadow-primary/40">
                  <div className="h-full w-full rounded-full bg-zinc-900 flex items-center justify-center text-3xl font-bold text-primary-foreground/80">
                    <img src={marlonRibeiro} alt="Foto de Marlon Ribeiro" className="rounded-full"/>
                  </div>
                </div>
                <div className="absolute -bottom-2 -right-1 rounded-full bg-emerald-500/90 px-3 py-1 text-[10px] font-semibold text-emerald-950 shadow-md flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-emerald-700 animate-pulse" />
                  Ativo
                </div>
              </div>

              <div className="text-center md:text-left">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground mb-1">
                  Neuroterapeuta & criador do método LDA
                </p>
                <h3 className="text-xl md:text-2xl font-bold">Marlon Ribeiro</h3>
                <p className="mt-1 text-xs md:text-sm text-muted-foreground max-w-xs">
                  Cristão, marido da Camilla há 13 anos e pai de Benjamim e Giovana.
                </p>
              </div>

              {/* Métricas / provas sociais */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full text-sm text-muted-foreground mt-2">
                <div className="flex items-start gap-3">
                  <Award className="h-5 w-5 text-primary mt-[2px]" />
                  <div>
                    <p className="text-foreground font-semibold text-sm">
                      Referência nacional
                    </p>
                    <p className="text-[11px] leading-snug">
                      Reconhecido em todo o país no combate ao vício em apostas.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Users className="h-5 w-5 text-primary mt-[2px]" />
                  <div>
                    <p className="text-foreground font-semibold text-sm">
                      Milhares alcançados
                    </p>
                    <p className="text-[11px] leading-snug">
                      Alcançou milhões com seus conteúdos e já ajudou centenas
                      diretamente.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <ShieldCheck className="h-5 w-5 text-primary mt-[2px]" />
                  <div>
                    <p className="text-foreground font-semibold text-sm">
                      Processo validado
                    </p>
                    <p className="text-[11px] leading-snug">
                      Método próprio voltado a reestruturar padrões mentais
                      ligados ao vício e à autossabotagem.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Coluna direita: narrativa + bullets */}
            <div className="relative md:w-[64%] text-sm md:text-base text-muted-foreground space-y-4 leading-relaxed">
              <p>
                Marlon transformou uma história marcada por dificuldades em uma{" "}
                <span className="font-semibold text-foreground">
                  missão que salva vidas diariamente
                </span>
                . Depois de anos na área financeira e sentindo que não cumpria
                seu propósito, mergulhou nos estudos da neurociência e desenvolveu
                um processo inovador para quem vive preso ao ciclo das apostas.
              </p>

              <p>
                O Método LDA foi construído para{" "}
                <span className="font-semibold text-foreground">
                  reestruturar padrões mentais ligados ao vício, impulsividade e
                  autossabotagem
                </span>
                , indo muito além do &quot;simplesmente parar de apostar&quot;.
                Em poucos meses, seus conteúdos alcançaram milhões, e centenas de
                pessoas voltaram a ter controle, estabilidade emocional e
                dignidade.
              </p>

              <p>
                Crescido em comunidade e enfrentando desafios intensos desde cedo,
                Marlon escolheu um caminho oposto ao que o ambiente oferecia.
                Mantendo princípios firmes e enorme determinação, tornou-se uma
                das maiores autoridades do país no assunto.
              </p>

              <div className="mt-4 rounded-2xl bg-card/80 border border-primary/20 px-4 py-3">
                <p className="text-xs font-semibold text-foreground mb-2 flex items-center gap-2">
                  <ArrowRight className="h-4 w-4 text-primary" />
                  Você vai aprender com alguém que:
                </p>
                <ul className="space-y-1.5 text-xs md:text-sm">
                  <li>
                    • Já esteve ao lado de quem perdeu tudo para as apostas — e
                    conhece a dor por trás dos números.
                  </li>
                  <li>
                    • Une neurociência, espiritualidade e técnica em um processo
                    estruturado, passo a passo.
                  </li>
                  <li>
                    • Tem uma missão clara:{" "}
                    <span className="font-semibold text-foreground">
                      tirar o máximo de pessoas possível do vício
                    </span>{" "}
                    e ajudá-las a reconstruir a própria história.
                  </li>
                </ul>
              </div>

              <p className="pt-2 text-xs md:text-sm text-muted-foreground/90 italic">
                Hoje, Marlon sustenta uma verdade forte:{" "}
                <span className="font-semibold text-foreground">
                  ninguém no Brasil tirou mais pessoas das apostas do que ele.
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
